import { SelectField, Submit, useForm } from '@ff-it/form';
import { activityFieldProps, useActivities } from 'components';
import { ProductTitle, type EmbeddedProduct } from 'modules/supplier/products';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Activity, EmbeddedCompany } from 'types';
import * as styles from './SupplierForm.css';

import { CompanyField, companyFieldProps } from 'modules/core/company/components';
import { SelectInstance } from 'react-select';

import useSWR from 'swr';
import { Icon } from 'components/Icon';
import { clearIcon } from 'styles/style.css';

type FormData = {
  activity: Activity | null;
  provider: EmbeddedCompany | null;
};

type SupplierFormProps = {
  product: EmbeddedProduct | null;
  setProduct: (p: EmbeddedProduct | null) => void;
  providerId: number | undefined;
  required?: boolean;
  partial?: boolean;
};

export function SupplierForm({ product, providerId, required, partial, setProduct }: SupplierFormProps): ReactNode {
  const providerFieldRef = useRef<SelectInstance<any>>(null);

  const isFirstRender = useRef<boolean>(true);
  const first = isFirstRender.current;
  isFirstRender.current = false;
  const form = useForm<FormData>();

  const allActivities = useActivities();
  const activities = product?.activities.length
    ? product.activities
    : allActivities?.filter((a) => a.kind === product?.kind) || [];

  // We fetch providers for this product even when filter provider is present.
  // Product provider relationship is compilcated we do this jsut to validate it.
  const { data: productProviders, isLoading } = useSWR<EmbeddedCompany[]>(
    product
      ? {
          url: `supplier/providers/`,
          method: 'GET',
          queryParams: { include_branches: true, product: product.id },
        }
      : null,
  );

  const providerOptions = useMemo(
    () => (providerId ? productProviders?.filter((p) => p.id === providerId) : productProviders),
    [productProviders, providerId],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: activities are in sync with product and form is stable
  useEffect(() => {
    if (first) {
      return;
    }

    let activity: Activity | null = null;
    if (product) {
      if (activities.length === 1) {
        activity = activities[0];
      } else if (product.kind === 'MEDIA') {
        // first standard
        activity = activities.find((a) => !a.non_standard) || null;
      }
    }
    // We reset activity always if product changes
    form.change('activity', activity);
  }, [product]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: form is stable, product in sync
  useEffect(() => {
    if (first) {
      return;
    }
    // providers are undefined while loading we don't want extra renders just bail
    if (!providerOptions) {
      return;
    }

    const currentProvider = form.getFieldState('provider')?.value;

    // Clear provider if no product selected
    if (!product) {
      if (currentProvider) {
        form.change('provider', null);
      }
      return;
    }

    // Auto-select single provider
    if (providerOptions.length === 1) {
      if (currentProvider?.id !== providerOptions[0].id) {
        form.change('provider', providerOptions[0]);
      }
      return;
    }

    // Keep current provider if it's still valid
    if (currentProvider && providerOptions.find((p) => p.id === currentProvider.id)) {
      return;
    }

    // Clear invalid provider
    if (currentProvider) {
      form.change('provider', null);
    }

    // Focus field regardless of current provider value
    providerFieldRef.current?.focus();
  }, [productProviders]);

  // Product not set and form is partial. Pick any activity and any supplier
  if (partial && !product) {
    return (
      <div className={styles.container}>
        <div className={styles.product}>
          <div>No product selected</div>
        </div>
        <SelectField
          label="Activity"
          name="activity"
          menuPortalTarget={document.body}
          // if partai and product is not Set
          options={allActivities}
          {...activityFieldProps}
          openMenuOnFocus
        />
        <CompanyField
          label="Provider"
          name="provider"
          menuPortalTarget={document.body}
          selectRef={providerFieldRef as any}
          openMenuOnFocus
          includeBranches
        />

        <Submit className="mt-auto" />
      </div>
    );
  }

  // product set and fields contrained or prdocut not set buty required
  const clearProduct = Boolean(product && (!required || partial));

  return (
    <div className={styles.container}>
      <div className={styles.product}>
        {product ? <ProductTitle product={product} size="sm" /> : <div>No product selected</div>}
        {clearProduct ? <Icon icon="circle-xmark" className={clearIcon} onClick={() => setProduct(null)} /> : null}
      </div>
      <SelectField
        label="Activity"
        name="activity"
        menuPortalTarget={document.body}
        options={activities}
        {...activityFieldProps}
        disabled={!product}
        openMenuOnFocus
        required
      />

      <SelectField<EmbeddedCompany>
        label="Provider"
        name="provider"
        menuPortalTarget={document.body}
        options={providerOptions}
        {...companyFieldProps}
        disabled={!product}
        selectRef={providerFieldRef}
        openMenuOnFocus
        isLoading={isLoading}
        required
      />

      <Submit className="mt-auto" />
    </div>
  );
}
